import { nanoid } from "nanoid";
import { use, useTransition } from "react";

import { HelpContext } from "@bokio/contexts/HelpContext/HelpContext";
import { FormGroup } from "@bokio/designsystem/components/Form";
import { InputField } from "@bokio/designsystem/components/Form/InputField";
import { SelectField } from "@bokio/designsystem/components/Form/SelectField";
import { TextAreaField } from "@bokio/designsystem/components/Form/TextAreaField";
import { LoadingSpinner } from "@bokio/designsystem/components/Loading/LoadingSpinner";
import { RadioFieldGroup } from "@bokio/designsystem/components/RadioFieldGroup/RadioFieldGroup";
import { Paragraph } from "@bokio/designsystem/components/TypographicElements/TypographicElements";
import { useDeviceQuery } from "@bokio/elements/DeviceQuery/useDeviceQuery";
import { FileUploader } from "@bokio/elements/FileUploader/FileUploader";
import { LinkButton } from "@bokio/elements/LinkButton/LinkButton";
import Markdown from "@bokio/elements/Markdown/Markdown";
import { useDisableGlobalFileDropZone } from "@bokio/hooks/useDisableGlobalFileDropZone/useDisableGlobalFileDropZone";
import { SettingsLangFactory } from "@bokio/lang";
import GeneralLangFactory from "@bokio/lang/GeneralLangFactory";
import * as m from "@bokio/mobile-web-shared/core/model/model";
import { formatMessage } from "@bokio/mobile-web-shared/core/utils/formatMessage";
import { filterByField } from "@bokio/shared/validation/entityValidator";
import { mergeClassNames } from "@bokio/utils/classes";

import { SensitiveInfoReminderLink } from "../SensitiveInfoReminderLink/SensitiveInfoReminderLink";

import type { MenuHelpMessageFormValues } from "./Help/Help";
import type { SelectFieldOption } from "@bokio/designsystem/components/Form/SelectField";
import type { FileWithPreview } from "@bokio/elements/FileUploader/components/FilePreview/FilePreview";
import type { FieldValidationResult, ValidatorResult } from "@bokio/shared/validation/entityValidator";
import type { Dispatch, FC, SetStateAction } from "react";

import * as styles from "./helpStyle.scss";

import HelpType = m.Contracts.HelpType;

interface HelpMessageFormProps {
	isOpened: boolean;
	modalView?: boolean;
	formData: MenuHelpMessageFormValues;
	setFormData: (reducer: (oldFormData: MenuHelpMessageFormValues) => MenuHelpMessageFormValues) => void;
	isLoggedIn: boolean;
	validation: ValidatorResult & {
		isValid: boolean;
		errors?: FieldValidationResult[] | undefined;
	};
	areas: m.Contracts.SupportArea[];
	contactTimePreferences?: m.Contracts.CallTimePreference[];
	files: FileWithPreview[];
	setFiles: Dispatch<SetStateAction<FileWithPreview[]>>;
}

const getTypeOption = (type: HelpType) => {
	const lang = GeneralLangFactory();
	switch (type) {
		case HelpType.Feedback:
			return lang.Help_Type_Feedback;
		case HelpType.Technincal:
			return lang.Help_Type_Technical;
		case HelpType.Question:
			return lang.Help_Type_Question;
		case HelpType.NotSet:
			throw new Error("Type NotSet is not supported");
	}
};

export const HelpMessageForm: FC<HelpMessageFormProps> = ({
	isOpened,
	modalView,
	validation,
	isLoggedIn,
	setFormData,
	formData,
	areas,
	contactTimePreferences,
	files,
	setFiles,
}) => {
	const { isMobile } = useDeviceQuery();
	useDisableGlobalFileDropZone();
	const [isTakingScreenshot, startTakingScreenshot] = useTransition();

	if (!isOpened) {
		return null;
	}

	const lang = GeneralLangFactory();
	const settingsLang = SettingsLangFactory();
	const selectedArea = areas.find(a => a.Key === formData.Area);
	const selectedCategory = selectedArea?.SupportCategories.find(c => c.Key === formData.Category);

	const getCallBackPreference = (): SelectFieldOption[] => {
		const preferenceDropDownValues: SelectFieldOption[] = [];

		contactTimePreferences?.forEach(element => {
			switch (element) {
				case m.Contracts.CallTimePreference.SoonAsPossible:
					preferenceDropDownValues.push({
						value: element,
						label: lang.Help_ChooseAPreferredCallTime_SoonAsPossible,
					});
					break;
				case m.Contracts.CallTimePreference.Morning:
					preferenceDropDownValues.push({
						value: element,
						label: lang.Help_ChooseAPreferredCallTime_Morning,
					});
					break;
				case m.Contracts.CallTimePreference.Afternoon:
					preferenceDropDownValues.push({
						value: element,
						label: lang.Help_ChooseAPreferredCallTime_Afternoon,
					});
					break;
			}

			return preferenceDropDownValues;
		});

		return preferenceDropDownValues;
	};

	const helpContext = use(HelpContext);
	const takeScreenshot = async () => {
		startTakingScreenshot(async () => {
			const html2canvas = (await import("html2canvas")).default;
			const currentSceneElement = helpContext?.screenshotElement;
			if (currentSceneElement == null) {
				return;
			}
			const canvas = await html2canvas(currentSceneElement);
			const b64 = await new Promise(resolve => canvas.toBlob(blob => blob && resolve(blob), "image/png"));
			if (!b64) {
				return;
			}
			const suffix = nanoid(10);
			const file = new File([b64 as BlobPart], `bokio_auto_screenshot_${suffix}.png`, { type: "image/png" });
			const fileWithPreview: FileWithPreview = Object.assign(file, { preview: URL.createObjectURL(file) });
			setFiles(prev => [...prev, fileWithPreview]);
		});
	};
	const screenshotTaken = files.some(f => f.name.startsWith("bokio_auto_screenshot_"));
	const clickToAttachScreenshotText = lang.click_to_attach_screenshot;
	const clickToAttachAnotherScreenshotText = lang.click_to_attach_another_screenshot;
	const screenshotText = formatMessage(
		screenshotTaken ? clickToAttachAnotherScreenshotText : clickToAttachScreenshotText,
		text => (
			<LinkButton onClick={() => !isTakingScreenshot && takeScreenshot()} testId="HelpMessageForm_TakeScreenshot">
				{text}
			</LinkButton>
		),
	);

	if (formData.IsCallBackRequest) {
		return (
			<div className={mergeClassNames(styles.helpFormWrapper, modalView && styles.helpFormWrapperModal)}>
				<FormGroup>
					<span>{settingsLang.Billing_RequestCall_Introduction}</span>
				</FormGroup>
				<FormGroup>
					<InputField
						onChange={name => setFormData(prev => ({ ...prev, Name: name }))}
						label={lang.YourName}
						value={formData.Name}
						errors={filterByField("Name", validation.errors)}
						testId="Help_MessageForm_Name"
					/>
				</FormGroup>
				<FormGroup>
					<InputField
						onChange={phoneNumber => setFormData(prev => ({ ...prev, PhoneNumber: phoneNumber }))}
						label={lang.PhoneNumber}
						value={formData.PhoneNumber}
						errors={filterByField("PhoneNumber", validation.errors)}
						testId="Help_MessageForm_PhoneNumber"
					/>
				</FormGroup>
				<FormGroup>
					<SelectField
						label={lang.Help_ChooseAPreferredCallTime}
						placeholder={lang.Help_ChooseAPreferredCallTime_PlaceHolder}
						options={getCallBackPreference()}
						onChange={preference => setFormData(prev => ({ ...prev, CallTimePreference: preference }))}
						value={formData.CallTimePreference}
						testId="Help_MessageForm_CallTimePreference"
					/>
				</FormGroup>
				{formData.DoesCompanyHavePhoneNumber && (
					<Markdown
						markdownContent={settingsLang.Billing_RequestCall_WrongNumber}
						deprecatedOptoutFromTypographicElements
					/>
				)}
			</div>
		);
	} else {
		return (
			<div className={mergeClassNames(styles.helpFormWrapper, modalView && styles.helpFormWrapperModal)}>
				{!modalView && <b>{lang.ContactSupport_action}</b>}
				<FormGroup>
					<InputField
						onChange={name => setFormData(prev => ({ ...prev, Name: name }))}
						label={lang.YourName}
						value={formData.Name}
						errors={filterByField("Name", validation.errors)}
						testId="Help_MessageForm_Name"
					/>
				</FormGroup>
				<FormGroup>
					<SelectField
						label={lang.Help_WhatIsYourQuestion}
						placeholder={lang.Help_ChooseAnArea}
						options={areas.map(area => ({ value: area.Key, label: area.Label }))}
						onChange={area =>
							setFormData(prev => ({
								...prev,
								Area: area,
								Category: m.Contracts.SupportFormCategory.NotSet,
								SubCategory: m.Contracts.SupportFormSubCategory.NotSet,
							}))
						}
						value={formData.Area}
						errors={filterByField("Area", validation.errors)}
						labelTestId="Help_MessageForm_Area_Label"
						testId="Help_MessageForm_Area"
					/>
					<SelectField
						label={lang.Help_ChooseACategory}
						placeholder={lang.Help_ChooseACategory}
						disabled={selectedArea?.SupportCategories === undefined || selectedArea.SupportCategories.length === 0}
						options={selectedArea?.SupportCategories.map(cat => ({ value: cat.Key, label: cat.Label })) ?? []}
						onChange={category =>
							setFormData(prev => ({
								...prev,
								Category: category,
								SubCategory: m.Contracts.SupportFormSubCategory.NotSet,
							}))
						}
						value={formData.Category}
						errors={filterByField("Category", validation.errors)}
						testId="Help_MessageForm_Category"
					/>
					{selectedCategory &&
						selectedCategory.SubCategories !== undefined &&
						selectedCategory.SubCategories.length > 0 && (
							<SelectField
								label={lang.Help_ChooseASubCategory}
								placeholder={lang.Help_ChooseASubCategory}
								options={selectedCategory.SubCategories.map(cat => ({ value: cat.Key, label: cat.Label }))}
								onChange={subcategory => setFormData(prev => ({ ...prev, SubCategory: subcategory }))}
								value={formData.SubCategory}
								errors={filterByField("SubCategory", validation.errors)}
								testId="Help_MessageForm_SubCategory"
							/>
						)}
				</FormGroup>
				{!isLoggedIn && (
					<FormGroup>
						<InputField
							onChange={email => setFormData(prev => ({ ...prev, Email: email }))}
							label={lang.Email}
							value={formData.Email}
							errors={filterByField("Email", validation.errors)}
							testId="Help_MessageForm_Email"
						/>
					</FormGroup>
				)}
				<FormGroup>
					<RadioFieldGroup
						name="Help_MessageForm_Type"
						testId="Help_MessageForm_RadioField_Type"
						label={lang.Help_Type}
						value={formData.Type}
						options={Object.keys(HelpType)
							// @ts-expect-error -- 2025-07-13 PontusH: noImplicitAny config rule has been enabled. Please fix this error if you are editing this file.
							.filter(k => HelpType[k] !== HelpType.NotSet)
							.map(k => ({
								// @ts-expect-error -- 2025-07-13 PontusH: noImplicitAny config rule has been enabled. Please fix this error if you are editing this file.
								label: getTypeOption(HelpType[k]),
								// @ts-expect-error -- 2025-07-13 PontusH: noImplicitAny config rule has been enabled. Please fix this error if you are editing this file.
								value: HelpType[k],
								// @ts-expect-error -- 2025-07-13 PontusH: noImplicitAny config rule has been enabled. Please fix this error if you are editing this file.
								testId: `Help_MessageForm_Type_${HelpType[k]}`,
								inputTestId: "Help_MessageForm_Type",
							}))}
						errors={filterByField("Type", validation.errors)}
						onChange={type => setFormData(prev => ({ ...prev, Type: type }))}
						layout={isMobile ? "vertical" : "horizontal"}
					/>
				</FormGroup>
				<FormGroup>
					<TextAreaField
						className={styles.textBox}
						value={formData.Message}
						onChange={message => setFormData(prev => ({ ...prev, Message: message }))}
						errors={filterByField("Message", validation.errors)}
						placeholder={lang.Help_HowCanWeHelpYou}
						minRows={7}
						testId="Help_MessageForm_Message"
						labelTestId="Help_MessageForm_Message_Label"
						hint={<SensitiveInfoReminderLink />}
					/>
				</FormGroup>
				<FileUploader
					label={lang.Help_UploadAttachments}
					small
					rejectAllOnAnyRejection
					allowMultiple
					acceptedFileTypes={["pdf", "jpg", "png", "gif", "webp", "si", "se", "heic", "csv", "tsv"]}
					onFileUpload={f => setFiles(prev => [...prev, ...f])}
					onFileDelete={f => setFiles(prev => prev.filter(p => p.name != f.name))}
					files={files}
					disablePreview={false}
					testId="Help_MessageForm_FileUploader"
				/>
				{!isTakingScreenshot && <Paragraph style={"caption-light"}>{screenshotText}</Paragraph>}
				{isTakingScreenshot && (
					<div className={styles.screenshotLoading}>
						<LoadingSpinner size="small" />
						<span>{lang.taking_screenshot}</span>
					</div>
				)}
			</div>
		);
	}
};
